import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RemixAvatarStack } from "./RemixAvatarStack";
import { RemixButton } from "./RemixButton";
import { Star, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToggleFavorite, useCheckFavorited } from "@/hooks/useFavorites";
import { cn } from "@/lib/utils";
import { getScoreColor } from "@/lib/scoreUtils";
import { PromptActionsMenu } from "@/components/shared/PromptActionsMenu";

const CATEGORY_COLORS: Record<string, string> = {
  'Planning': 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  'Strategy': 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30',
  'Research': 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30',
  'Drafting': 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  'Analysis': 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
  'Creative': 'bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/30',
  'Brainstorming': 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/30',
  'Summarizing': 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  'Unknown': 'bg-muted text-muted-foreground border-border'
};

interface Remixer {
  user_id: string;
  user_initials: string;
}

export interface FeedPrompt {
  score_id: string;
  submission_id?: string;
  score_total: number;
  detected_type: string;
  best_prompt: string | null;
  user_id: string;
  user_name: string;
  user_initials: string;
  created_at: string;
  favorite_count: number;
  evaluation_markdown?: string | null;
  remix_count: number;
  recent_remixers: Remixer[];
  tags?: string[];
}

interface FeedCardProps {
  prompt: FeedPrompt;
  onViewDetail?: (prompt: FeedPrompt) => void;
  onRemix?: (prompt: FeedPrompt) => void;
  onViewRemixers?: (prompt: FeedPrompt) => void;
  onHidePrompt?: (prompt: FeedPrompt) => void;
  onDeletePrompt?: (prompt: FeedPrompt) => void;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getPromptSummary(prompt: FeedPrompt): string {
  if (prompt.evaluation_markdown) {
    try {
      const parsed = JSON.parse(prompt.evaluation_markdown);
      if (parsed.prompt_summary) return parsed.prompt_summary;
    } catch {}
  }
  return 'Provides structured guidance for your task';
}

function getPromptTitle(prompt: FeedPrompt): string | null {
  if (prompt.evaluation_markdown) {
    try {
      const parsed = JSON.parse(prompt.evaluation_markdown);
      if (parsed.prompt_title) return parsed.prompt_title;
    } catch {}
  }
  return null;
}

function getPromptTags(prompt: FeedPrompt): string[] {
  // Use tags from RPC (relational database) if available
  if (prompt.tags && Array.isArray(prompt.tags)) {
    return prompt.tags.slice(0, 3); // Limit to 3 tags to fit on one line
  }
  return [];
}

export function FeedCard({ prompt, onViewDetail, onRemix, onViewRemixers, onHidePrompt, onDeletePrompt }: FeedCardProps) {
  const { user } = useAuth();
  const toggleMutation = useToggleFavorite();
  const { data: isFavorited, isLoading: isFavLoading } = useCheckFavorited(user?.id, prompt.score_id);

  const handleFavoriteToggle = () => {
    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }
    toggleMutation.mutate({ userId: user.id, scoreId: prompt.score_id, isFavorited: !!isFavorited });
  };

  const trackAction = async (actionType: 'copy' | 'share_chatgpt') => {
    if (!user) return;
    try {
      await (supabase.from('prompt_actions' as any) as any).insert({
        score_id: prompt.score_id,
        user_id: user.id,
        action_type: actionType
      });
    } catch (err) {
      console.error('Failed to track action:', err);
    }
  };

  const handleCopy = async () => {
    if (prompt.best_prompt) {
      await navigator.clipboard.writeText(prompt.best_prompt);
      toast.success('Prompt copied to clipboard');
      trackAction('copy');
    }
  };

  const handleShare = () => {
    trackAction('share_chatgpt');
  };

  const chatGptUrl = prompt.best_prompt ?
  `https://chatgpt.com/?prompt=${encodeURIComponent(prompt.best_prompt)}` :
  '#';

  return (
    <div
      className="border-b border-border/30 p-4 md:p-5 hover:bg-muted/10 transition-colors cursor-pointer pt-[2px]"
      onClick={() => onViewDetail?.(prompt)}>
      
      <div>
        <div className="min-w-0">
          {/* Row 1: Timestamp pinned right */}
          <div className="flex justify-end">
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(prompt.created_at)}
            </span>
          </div>

          {/* Row 2: Score + "Prompt Score" label (left) + Category badge (right) */}
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-baseline gap-1.5">
              <span className={`font-bold text-xl ${getScoreColor(prompt.score_total)}`}>
                {prompt.score_total}
              </span>
              <span className="text-sm text-muted-foreground/60">/100</span>
              <span className="text-xs text-muted-foreground ml-1">Prompt Score</span>
            </div>
            <Badge
              variant="outline"
              className={`text-xs border flex-shrink-0 rounded-md ${CATEGORY_COLORS[prompt.detected_type] || CATEGORY_COLORS['Unknown']}`}>
              
              {prompt.detected_type || 'Unknown'}
            </Badge>
          </div>

          {/* Title (bold) + Summary text (no box, high contrast) - 15px to match Twitter mobile */}
          <div className="mt-3">
            {getPromptTitle(prompt) &&
            <h3 className="text-[17px] font-semibold text-foreground mb-1">
                {getPromptTitle(prompt)}
              </h3>
            }
            <p className="text-[15px] text-foreground/80 leading-relaxed">
              {getPromptSummary(prompt)}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-3" onClick={(e) => e.stopPropagation()}>
            {/* Left side: existing action buttons */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-8 px-2 gap-1", isFavLoading && "opacity-50")}
                onClick={handleFavoriteToggle}
                disabled={toggleMutation.isPending || isFavLoading}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}>
                
                <Star className={cn(
                  "h-4 w-4",
                  isFavorited ?
                  "fill-yellow-500 text-yellow-500" :
                  "text-muted-foreground"
                )} />
                {prompt.favorite_count > 0 &&
                <span className="text-xs text-muted-foreground">{prompt.favorite_count}</span>
                }
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleCopy}
                title="Copy prompt">
                
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                asChild
                title="Try in ChatGPT"
                onClick={handleShare}>
                
                <a href={chatGptUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
              {prompt.submission_id &&
              <PromptActionsMenu
                submissionId={prompt.submission_id}
                promptUserId={prompt.user_id}
                isPublic={true}
                onHide={() => onHidePrompt?.(prompt)}
                onDelete={() => onDeletePrompt?.(prompt)} />

              }
            </div>

            {/* Right side: remixer avatars + remix button */}
            <div className="flex items-center gap-2">
              {prompt.remix_count > 0 &&
              <RemixAvatarStack
                remixers={prompt.recent_remixers}
                totalCount={prompt.remix_count}
                onClick={() => onViewRemixers?.(prompt)} />

              }
              <RemixButton onClick={() => onRemix?.(prompt)} />
            </div>
          </div>
        </div>
      </div>
    </div>);

}